import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import apiService from '../services/api';
import socketService from '../services/socketService';

// Define types
type User = {
  id: string;
  name: string;
  email: string;
  role: string;
  activated?: boolean;
  passwordSet?: boolean;
};

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  error: string | null;
};

// Create context with default values
export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: () => {},
  error: null,
});

// Mock API for authentication (replace with real API calls later)
const mockLogin = async (email: string, password: string): Promise<{ token: string }> => {
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 800));
  
  // Demo credentials for testing
  if (email === 'admin@example.com' && password === 'password') {
    // Create a mock JWT token with user data
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjEiLCJuYW1lIjoiQWRtaW4gVXNlciIsImVtYWlsIjoiYWRtaW5AZXhhbXBsZS5jb20iLCJyb2xlIjoiTUQiLCJpYXQiOjE2MTYxNjI4MTcsImV4cCI6MTY0NzcwMjgxN30.3YC1EB3gHl4GHlKIErBVEBB3UXpSh0CpvQQFcH1mYnU';
    return { token };
  } else if (email === 'manager@example.com' && password === 'password') {
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjIiLCJuYW1lIjoiSGFtYWRpIFNpbWJhIiwiZW1haWwiOiJtYW5hZ2VyQGV4YW1wbGUuY29tIiwicm9sZSI6IlByb2plY3QgTWFuYWdlciIsImFjdGl2YXRlZCI6dHJ1ZSwicGFzc3dvcmRTZXQiOiJ0cnVlIiwiaWF0IjoxNjE2MTYyODE3LCJleHAiOjE2NDc3MDI4MTd9.8YuI1LgqOKPw0QyZjNO-xAVuUJn9sMhcbVzwcn1XBtM';
    return { token };
  } else if (email === 'accountant@example.com' && password === 'password') {
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjMiLCJuYW1lIjoiTWljaGFlbCBNdXNoaSIsImVtYWlsIjoiYWNjb3VudGFudEBleGFtcGxlLmNvbSIsInJvbGUiOiJBY2NvdW50YW50IiwiYWN0aXZhdGVkIjp0cnVlLCJwYXNzd29yZFNldCI6InRydWUiLCJpYXQiOjE2MTYxNjI4MTcsImV4cCI6MTY0NzcwMjgxN30.rYTYB6FeJ8bxR9lOHRHZEEsBDrD5vyTOLzq6jbGjqYI';
    return { token };
  } else if (email === 'cashier@example.com' && password === 'password') {
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjkiLCJuYW1lIjoiSmFtZXMgTXdhbmdpIiwiZW1haWwiOiJjYXNoaWVyQGV4YW1wbGUuY29tIiwicm9sZSI6IkNhc2hpZXIiLCJhY3RpdmF0ZWQiOiJ0cnVlIiwicGFzc3dvcmRTZXQiOiJ0cnVlIiwiaWF0IjoxNjE2MTYyODE3LCJleHAiOjE2NDc3MDI4MTd9.1JLOmPNMOkLBFJV9XFKS8ApdMjQdEFu9FpIW9oEHf0M';
    return { token };
  } else if (email === 'logistics@example.com' && password === 'password') {
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjYiLCJuYW1lIjoiR3JhY2UgTnlhbWJvIiwiZW1haWwiOiJsb2dpc3RpY3NAZXhhbXBsZS5jb20iLCJyb2xlIjoiU3RvcmUtS2VlcGVyIChMb2dpc3RpY3MpIiwiYWN0aXZhdGVkIjp0cnVlLCJwYXNzd29yZFNldCI6InRydWUiLCJpYXQiOjE2MTYxNjI4MTcsImV4cCI6MTY0NzcwMjgxN30.1JLOmPNMOkLBFJV9XFKS8ApdMjQdEFu9FpIW9oEHf0M';
    return { token };
  } else if (email === 'procurement@example.com' && password === 'password') {
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjEwIiwibmFtZSI6IlJvYmVydCBNd2FuemEiLCJlbWFpbCI6InByb2N1cmVtZW50QGV4YW1wbGUuY29tIiwicm9sZSI6IlByb2N1cmVtZW50IiwiYWN0aXZhdGVkIjp0cnVlLCJwYXNzd29yZFNldCI6InRydWUiLCJpYXQiOjE2MTYxNjI4MTcsImV4cCI6MTY0NzcwMjgxN30.1JLOmPNMOkLBFJV9XFKS8ApdMjQdEFu9FpIW9oEHf0M';
    return { token };
  } else if (email === 'supervisor@example.com' && password === 'password') {
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY4OTA3NmVlOWY1NzFlZWYxNDg1OGVlZDMiLCJuYW1lIjoiR3JhY2UgTnltYWJvIiwiZW1haWwiOiJzdXBlcnZpc29yQGV4YW1wbGUuY29tIiwicm9sZSI6IlN1cGVydmlzb3IiLCJhY3RpdmF0ZWQiOiJ0cnVlIiwicGFzc3dvcmRTZXQiOiJ0cnVlIiwiaWF0IjoxNjE2MTYyODE3LCJleHAiOjE2NDc3MDI4MTd9.1JLOmPNMOkLBFJV9XFKS8ApdMjQdEFu9FpIW9oEHf0M';
    return { token };

  } else if (email === 'newuser@example.com' && password === 'password') {
    // This user is not activated yet
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjExIiwibmFtZSI6Ik5ldyBVc2VyIiwiZW1haWwiOiJuZXd1c2VyQGV4YW1wbGUuY29tIiwicm9sZSI6IlN1cGVydmlzb3IiLCJhY3RpdmF0ZWQiOiJmYWxzZSIsInBhc3N3b3JkU2V0IjoiZmFsc2UiLCJpYXQiOjE2MTYxNjI4MTcsImV4cCI6MTY0NzcwMjgxN30.1JLOmPNMOkLBFJV9XFKS8ApdMjQdEFu9FpIW9oEHf0M';
    return { token };
  } else if (email === 'hradmin@example.com' && password === 'password') {
    // HR (Admin) user
    // JWT with "role": "HR (Admin)"
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjgiLCJuYW1lIjoiQWduZXMgTXdhc2ViYSIsImVtYWlsIjoiaHJhZG1pbkBleGFtcGxlLmNvbSIsInJvbGUiOiJIUiAoQWRtaW4pIiwiYWN0aXZhdGVkIjp0cnVlLCJwYXNzd29yZFNldCI6dHJ1ZSwiaWF0IjoxNjE2MTYyODE3LCJleHAiOjE2NDc3MDI4MTd9.8YuI1LgqOKPw0QyZjNO-xAVuUJn9sMhcbVzwcn1XBtM';
    return { token };
  } else if (email === 'logistics@example.com' && password === 'password') {
    // Store-Keeper (Logistics) user
    // JWT with "role": "Store-Keeper (Logistics)"
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjYiLCJuYW1lIjoiR3JhY2UgTnltYWJvIiwiZW1haWwiOiJsb2dpc3RpY3NAZXhhbXBsZS5jb20iLCJyb2xlIjoiU3RvcmUtS2VlcGVyIChMb2dpc3RpY3MpIiwiYWN0aXZhdGVkIjp0cnVlLCJwYXNzd29yZFNldCI6dHJ1ZSwiaWF0IjoxNjE2MTYyODE3LCJleHAiOjE2NDc3MDI4MTd9.8YuI1LgqOKPw0QyZjNO-xAVuUJn9sMhcbVzwcn1XBtM';
    return { token };
  }
  
  throw new Error('Invalid credentials');
};

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode<User>(token);
        setUser(decoded);
        
        // Connect to Socket.IO for real-time notifications
        socketService.connect(decoded.id);
      } catch (error) {
        // Token is invalid
        localStorage.removeItem('token');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);
      
      // Use real backend API
      const response = await apiService.login(email, password);
      const { token } = response.data;
      
      localStorage.setItem('token', token);
      const decoded = jwtDecode<User>(token);
      
      // Check if user is activated
      if (decoded.activated === false) {
        setError('Your account is not activated. Please check your email for the activation link or contact your administrator.');
        localStorage.removeItem('token');
        return;
      }
      
      // Check if password is set
      if (decoded.passwordSet === false) {
        setError('Please set your password first. Check your email for the activation link.');
        localStorage.removeItem('token');
        return;
      }
      
      setUser(decoded);
      
      // Connect to Socket.IO for real-time notifications
      socketService.connect(decoded.id);
      
      // Redirect based on user role
      if (decoded.role === 'Cashier') {
        navigate('/cashier');
      } else if (decoded.role === 'Project Manager') {
        navigate('/manager');
      } else if (decoded.role === 'Supervisor') {
        navigate('/supervisor');
      } else if (decoded.role === 'HR (Admin)') {
        navigate('/hr');
      } else if (decoded.role === 'Accountant') {
        navigate('/accountant');
      } else if (decoded.role === 'Store-Keeper (Logistics)') {
        navigate('/storekeeper');
      } else if (decoded.role === 'MD' || decoded.role === 'TD') {
        navigate('/'); // MD/TD users go to main route
      } else {
        navigate('/');
      }
    } catch (error: any) {
      setError(error.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    socketService.disconnect();
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 